import type { DynamicCard as CustomElement } from "@nosto/web-components"
import { JSX } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { Props } from "@/types"

type DynamicCardProps = Props<CustomElement> &
  JSX.IntrinsicElements["span"] & {
    preferredColor?: string
    showOnlyClearanceSwatches?: boolean
    orderRegularSwatchesFirst?: boolean
  }

const DYNAMIC_CARD_LOADED_EVENT = "@nosto/DynamicCard/loaded"
const CLEARANCE_FILTER_STATE_ATTR = "data-clearance-filter-state"
const SWATCH_NORMALIZATION_STATE_ATTR = "data-swatch-normalization-state"
const CARD_READINESS_STATE_ATTR = "data-card-readiness-state"
const PRODUCT_IMAGE_STATE_ATTR = "data-product-image-state"
const CARD_READINESS_TIMEOUT_MS = 5000
const NON_SELECTABLE_SWATCH_ATTRS = ["data-disabled", "data-unavailable", "data-sold-out", "data-coming-soon"]

function isSelectableSwatch(swatch: HTMLElement) {
  return !swatch.hidden && NON_SELECTABLE_SWATCH_ATTRS.every(attr => !swatch.hasAttribute(attr))
}

function hasVariantChangeMetadata(swatch: HTMLElement) {
  return Boolean(swatch.dataset.img && swatch.dataset.url)
}

function selectSwatch(card: HTMLElement, swatch: HTMLElement) {
  const swatchRoot = swatch.closest("product-swatches") ?? card
  const swatches = Array.from(swatchRoot.querySelectorAll<HTMLElement>("[data-swatch]"))

  swatches.forEach(candidate => {
    const isSelected = candidate === swatch
    candidate.toggleAttribute("data-active", isSelected)
    candidate.setAttribute("aria-selected", isSelected ? "true" : "false")
    candidate.setAttribute("aria-checked", isSelected ? "true" : "false")
    candidate.setAttribute("tabindex", isSelected ? "0" : "-1")

    const radio = candidate.querySelector<HTMLInputElement>('input[type="radio"]')
    if (radio) {
      radio.checked = isSelected
    }
  })
}

function dispatchVariantChange(card: HTMLElement, swatch: HTMLElement) {
  const productCard = card.querySelector("product-card")
  productCard?.dispatchEvent(
    new CustomEvent("variant-change", {
      detail: {
        imageUrl: swatch.dataset.img,
        productUrl: swatch.dataset.url,
        price: swatch.dataset.price,
        compareAtPrice: swatch.dataset.compare
      }
    })
  )
}

function applyPreferredColor(card: HTMLElement, preferredColor: string) {
  const normalizedColor = preferredColor.toLowerCase()
  const swatches = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]"))
  const swatch = swatches.find(s => {
    const label = s.getAttribute("data-option-value") || s.querySelector("img")?.getAttribute("alt") || ""
    return label.toLowerCase().includes(normalizedColor) && isSelectableSwatch(s) && hasVariantChangeMetadata(s)
  })

  if (!swatch) return

  selectSwatch(card, swatch)
  dispatchVariantChange(card, swatch)
}

function showOnlyClearanceSwatches(card: HTMLElement) {
  const swatchLists = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch-list]"))

  for (const list of swatchLists) {
    const swatches = Array.from(list.querySelectorAll<HTMLElement>("[data-swatch]"))
    const hasClearanceMetadata = swatches.some(swatch => swatch.dataset.clearance !== undefined)
    if (!hasClearanceMetadata) continue

    swatches.forEach(swatch => {
      swatch.hidden = swatch.dataset.clearance !== "true"
    })

    // No clearance color is buyable (they're all sold out): the list would render empty or show
    // only a disabled chip. Hide those and reveal the card's displayed/default color as a single
    // fallback swatch so the tile matches its product image. `ensureActiveSwatch` pre-selects it.
    if (!swatches.some(isSelectableSwatch)) {
      swatches.forEach(swatch => {
        swatch.hidden = true
      })
      const fallbackSwatch = swatches.find(swatch => swatch.hasAttribute("data-active")) ?? swatches[0]
      if (fallbackSwatch) {
        fallbackSwatch.hidden = false
      }
    }
  }
}

function orderRegularSwatchesFirst(card: HTMLElement) {
  const swatchLists = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch-list]"))

  for (const list of swatchLists) {
    const swatches = Array.from(list.querySelectorAll<HTMLElement>("[data-swatch]"))
    const hasClearanceMetadata = swatches.some(swatch => swatch.dataset.clearance !== undefined)
    if (!hasClearanceMetadata) continue

    swatches.forEach(swatch => {
      swatch.hidden = false
    })
    const regularSwatches = swatches.filter(swatch => swatch.dataset.clearance !== "true")
    const clearanceSwatches = swatches.filter(swatch => swatch.dataset.clearance === "true")
    regularSwatches.concat(clearanceSwatches).forEach(swatch => list.appendChild(swatch))
  }
}

function hasClearanceMetadata(card: HTMLElement) {
  return Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]")).some(
    swatch => swatch.dataset.clearance !== undefined
  )
}

/**
 * Current storefront locale prefix from the URL, e.g. "/fr-ca" on /fr-ca/collections/...
 * Returns "" on the primary locale (no prefix).
 */
function getCurrentLocalePrefix() {
  const match = globalThis.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/i)
  return match ? `/${match[1]}` : ""
}

/**
 * The @nosto/web-components DynamicCard fetches markup from window.Shopify.shop (the
 * *.myshopify.com domain), which does not serve secondary locales — e.g.
 * `…myshopify.com/fr-ca/products/X` 301-redirects to the primary `/en-ca/…`. So on FR
 * PLPs the card arrives in English with wrong-locale PDP links. Detect that by comparing
 * the loaded card's product link against the active locale prefix.
 */
function cardLocaleMismatch(card: HTMLElement) {
  const localePrefix = getCurrentLocalePrefix()
  if (!localePrefix) return false

  const href = card.querySelector<HTMLAnchorElement>('a[href*="/products/"]')?.getAttribute("href")
  if (!href) return false

  const { pathname } = new URL(href, globalThis.location.origin)
  return !pathname.startsWith(`${localePrefix}/`)
}

async function reloadCardFromCurrentOrigin(
  card: HTMLElement,
  props: Pick<DynamicCardProps, "handle" | "template" | "variantId">
) {
  if (!props.handle || !props.template) return false

  // Re-fetch from the current origin (the public domain) with the locale prefix so the
  // markup matches the active storefront locale instead of the myshopify primary locale.
  const url = new URL(`${getCurrentLocalePrefix()}/products/${props.handle}`, globalThis.location.origin)
  url.searchParams.set("view", props.template)
  url.searchParams.set("layout", "none")
  if (props.variantId) {
    url.searchParams.set("variant", props.variantId)
  }

  const response = await fetch(url.href, {
    cache: "no-store",
    credentials: "same-origin"
  })
  if (!response.ok) return false

  const html = await response.text()
  if (/<(body|html)/i.test(html)) return false

  card.innerHTML = html
  return true
}

function ensureActiveSwatch(card: HTMLElement) {
  const activeSwatch = card.querySelector<HTMLElement>("[data-swatch][data-active]")
  const firstUsableSwatch = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]")).find(
    s => isSelectableSwatch(s) && hasVariantChangeMetadata(s)
  )
  const swatch =
    activeSwatch && isSelectableSwatch(activeSwatch) && hasVariantChangeMetadata(activeSwatch)
      ? activeSwatch
      : firstUsableSwatch

  if (swatch) {
    selectSwatch(card, swatch)
    dispatchVariantChange(card, swatch)
  }
}

function setReadinessState(card: HTMLElement, state: "pending" | "ready") {
  if (card.getAttribute(CARD_READINESS_STATE_ATTR) !== state) {
    card.setAttribute(CARD_READINESS_STATE_ATTR, state)
  }
}

function hasRenderedPrice(productCard: HTMLElement) {
  const priceWrapper = productCard.querySelector<HTMLElement>("[data-price-wrapper]")
  if (!priceWrapper) return true

  const priceComponent = priceWrapper.querySelector<HTMLElement>("price-component")
  if (priceComponent?.getAttribute("price")) return true

  return Boolean(priceWrapper.textContent?.trim())
}

function hasReadyProductImage(productCard: HTMLElement) {
  const image = productCard.querySelector<HTMLImageElement>("[data-product-image]")
  if (!image) return true

  const imageWrapper = image.closest<HTMLElement>(".product-card__image-wrapper")
  if (imageWrapper?.getAttribute(PRODUCT_IMAGE_STATE_ATTR) === "ready") return true

  return image.complete && image.naturalWidth > 0
}

function hasReadyActiveSwatch(card: HTMLElement) {
  const swatches = Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]"))
  if (swatches.length === 0) return true

  return swatches.some(swatch => swatch.hasAttribute("data-active") && isSelectableSwatch(swatch))
}

// Whether any swatch could still become the active one. When a (clearance-filtered) card has
// no selectable swatch at all — e.g. its only clearance color is sold out — there is nothing to
// wait for, so the active-swatch readiness gate below is skipped; otherwise such cards would sit
// behind the skeleton until the readiness fallback (~5s) fires.
function hasSelectableSwatch(card: HTMLElement) {
  return Array.from(card.querySelectorAll<HTMLElement>("[data-swatch]")).some(isSelectableSwatch)
}

function isCardReady(card: HTMLElement, requireActiveSwatch: boolean) {
  const productCard = card.querySelector<HTMLElement>("product-card")
  if (!productCard) return false
  if (!hasRenderedPrice(productCard)) return false
  if (card.getAttribute(SWATCH_NORMALIZATION_STATE_ATTR) === "pending") return false
  if (requireActiveSwatch && hasSelectableSwatch(card) && !hasReadyActiveSwatch(card)) return false
  return hasReadyProductImage(productCard)
}

function DynamicCardSkeleton() {
  return (
    <div className="nosto-dynamic-card-skeleton" aria-hidden="true">
      <div className="nosto-dynamic-card-skeleton__image" />
      <div className="nosto-dynamic-card-skeleton__swatches">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="nosto-dynamic-card-skeleton__line" />
      <div className="nosto-dynamic-card-skeleton__line nosto-dynamic-card-skeleton__line--short" />
    </div>
  )
}

/**
 * A custom element wrapper that renders a product by fetching the markup from Shopify based on the provided handle and template.
 *
 * This component is designed to be used in a Shopify environment and fetches product data dynamically.
 */
export default function DynamicCard({
  preferredColor,
  showOnlyClearanceSwatches: onlyClearanceSwatches,
  orderRegularSwatchesFirst: regularSwatchesFirst,
  children,
  ...props
}: DynamicCardProps) {
  const cardRef = useRef<HTMLElement>(null)
  const { handle, template, variantId } = props

  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    let didReloadFromCurrentOrigin = false
    let didReachReadinessFallback = false
    let localeReloadInFlight = false
    let watchedImage: HTMLImageElement | null = null
    // The @nosto/web-components DynamicCard fetches its markup from the myshopify domain. When
    // that fetch fails (transient errors / rate-limiting under cold-load concurrency) the web
    // component silently leaves our skeleton in place — no markup, no `loaded` event and no
    // retry — so the card would shimmer forever. Track the load attempt so we can recover the
    // card from the current origin instead (see recoverMissingMarkup).
    let didObserveLoading = card.hasAttribute("loading")
    let didAttemptMarkupRecovery = false
    const requiresSwatchNormalization = Boolean(onlyClearanceSwatches || regularSwatchesFirst)
    const requireActiveSwatch = Boolean(onlyClearanceSwatches)
    let readinessFallbackTimer: number | undefined = window.setTimeout(() => {
      didReachReadinessFallback = true
      syncCardReadiness()
      maybeRecoverMissingMarkup()
    }, CARD_READINESS_TIMEOUT_MS)

    const clearReadinessFallback = () => {
      if (readinessFallbackTimer === undefined) return
      window.clearTimeout(readinessFallbackTimer)
      readinessFallbackTimer = undefined
    }

    const syncCardReadiness = () => {
      const isReady = didReachReadinessFallback || (!localeReloadInFlight && isCardReady(card, requireActiveSwatch))
      setReadinessState(card, isReady ? "ready" : "pending")
      if (isReady) {
        clearReadinessFallback()
      }

      const image = card.querySelector<HTMLImageElement>("[data-product-image]")
      const productCard = image?.closest<HTMLElement>("product-card")
      if (image && image !== watchedImage && productCard && !hasReadyProductImage(productCard)) {
        watchedImage = image
        image.addEventListener("load", syncCardReadiness, { once: true })
        image.addEventListener("error", syncCardReadiness, { once: true })
      }
    }

    // Swatch normalization + readiness flips, shared by the loaded-event path (updateCard) and
    // the failed-load recovery path (recoverMissingMarkup) so both finalize a card identically.
    const finalizeCard = () => {
      if (onlyClearanceSwatches) {
        showOnlyClearanceSwatches(card)
      } else if (regularSwatchesFirst) {
        orderRegularSwatchesFirst(card)
      }
      if (preferredColor) {
        applyPreferredColor(card, preferredColor)
      }
      if (onlyClearanceSwatches) {
        ensureActiveSwatch(card)
        card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "ready")
      }
      if (onlyClearanceSwatches || regularSwatchesFirst) {
        card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "ready")
      }
      syncCardReadiness()
    }

    // When the web component never delivered markup, re-fetch the card from the current origin
    // (the locale-correct public domain — the same path used for the locale-mismatch reload). If
    // even that yields no card, clear the skeleton so the cell stops shimmering instead of hanging.
    const recoverMissingMarkup = async () => {
      localeReloadInFlight = true
      setReadinessState(card, "pending")
      let recovered = false
      try {
        recovered = await reloadCardFromCurrentOrigin(card, { handle, template, variantId })
      } catch {
        recovered = false
      } finally {
        localeReloadInFlight = false
      }

      if (recovered && card.querySelector("product-card")) {
        // We have already fetched from the current origin; skip updateCard's locale reload.
        didReloadFromCurrentOrigin = true
        finalizeCard()
        return
      }

      // Genuinely unavailable on the storefront too: stop the infinite shimmer.
      card.innerHTML = ""
      if (onlyClearanceSwatches) {
        card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "ready")
      }
      if (onlyClearanceSwatches || regularSwatchesFirst) {
        card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "ready")
      }
      didReachReadinessFallback = true
      syncCardReadiness()
    }

    const maybeRecoverMissingMarkup = () => {
      if (didAttemptMarkupRecovery) return
      if (card.querySelector("product-card")) return
      if (card.hasAttribute("loading")) {
        didObserveLoading = true
        return
      }
      // Only recover once the web component has actually finished a load attempt. The readiness
      // fallback is the backstop for the race where we attach after the loading toggle.
      if (!didObserveLoading && !didReachReadinessFallback) return
      if (!handle || !template) return
      didAttemptMarkupRecovery = true
      void recoverMissingMarkup()
    }

    setReadinessState(card, "pending")
    if (onlyClearanceSwatches || regularSwatchesFirst) {
      card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "pending")
      if (onlyClearanceSwatches) {
        card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "pending")
      }
    }

    const readinessObserver = new MutationObserver(() => {
      syncCardReadiness()
      maybeRecoverMissingMarkup()
    })
    readinessObserver.observe(card, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true
    })

    const updateCard = async () => {
      const hasLoadedMarkup = Boolean(card.querySelector("product-card"))
      if (requiresSwatchNormalization && !hasLoadedMarkup) {
        syncCardReadiness()
        return
      }

      // The web component loads markup from the myshopify domain, which collapses secondary
      // locales to the primary one (see cardLocaleMismatch). Re-fetch from the current origin
      // so FR PLPs show French cards with French PDP links. Shares didReloadFromCurrentOrigin
      // with the clearance reload below so a card is only re-fetched once.
      if (hasLoadedMarkup && !didReloadFromCurrentOrigin && cardLocaleMismatch(card)) {
        didReloadFromCurrentOrigin = true
        // Hold the readiness gate so the wrong-locale card never paints: keep the shimmer
        // skeleton until the localized markup is in place, for a single paint (skeleton → FR).
        localeReloadInFlight = true
        setReadinessState(card, "pending")
        try {
          await reloadCardFromCurrentOrigin(card, { handle, template, variantId })
        } catch {
          // Keep the web-component markup if the localized refetch fails.
        } finally {
          localeReloadInFlight = false
        }
      }

      if (onlyClearanceSwatches && hasLoadedMarkup && !hasClearanceMetadata(card)) {
        card.setAttribute(SWATCH_NORMALIZATION_STATE_ATTR, "pending")
        if (onlyClearanceSwatches) {
          card.setAttribute(CLEARANCE_FILTER_STATE_ATTR, "pending")
        }
        if (didReloadFromCurrentOrigin) return
        didReloadFromCurrentOrigin = true
        let didReloadSuccessfully = false
        try {
          didReloadSuccessfully = await reloadCardFromCurrentOrigin(card, { handle, template, variantId })
        } catch {
          didReloadSuccessfully = false
        }
        if (!didReloadSuccessfully || !hasClearanceMetadata(card)) return
      }
      finalizeCard()
    }
    card.addEventListener(DYNAMIC_CARD_LOADED_EVENT, updateCard)
    updateCard()
    syncCardReadiness()

    return () => {
      clearReadinessFallback()
      readinessObserver.disconnect()
      card.removeEventListener(DYNAMIC_CARD_LOADED_EVENT, updateCard)
      card.removeAttribute(CLEARANCE_FILTER_STATE_ATTR)
      card.removeAttribute(SWATCH_NORMALIZATION_STATE_ATTR)
      card.removeAttribute(CARD_READINESS_STATE_ATTR)
    }
  }, [preferredColor, onlyClearanceSwatches, regularSwatchesFirst, handle, template, variantId])

  return (
    <nosto-dynamic-card
      ref={cardRef}
      data-clearance-filter-state={onlyClearanceSwatches ? "pending" : undefined}
      data-swatch-normalization-state={onlyClearanceSwatches || regularSwatchesFirst ? "pending" : undefined}
      data-card-readiness-state="pending"
      {...props}
    >
      {children ?? <DynamicCardSkeleton />}
    </nosto-dynamic-card>
  )
}

declare module "preact/jsx-runtime" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "nosto-dynamic-card": DynamicCardProps
    }
  }
}
